
import React from "react";
import { User } from "@/types";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useIsMobile } from "@/hooks/use-mobile";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface PlayerPresenceSelectorProps {
  players: User[];
  selectedPlayerIds: string[];
  onPlayerToggle: (playerId: string) => void;
  currentUserId?: string;
  isLoading?: boolean;
}

export const PlayerPresenceSelector = ({ 
  players, 
  selectedPlayerIds, 
  onPlayerToggle,
  currentUserId,
  isLoading = false
}: PlayerPresenceSelectorProps) => {
  const isMobile = useIsMobile();
  
  // Always sort the current user to the top
  const sortedPlayers = React.useMemo(() => {
    return [...players].sort((a, b) => {
      if (a.id === currentUserId) return -1;
      if (b.id === currentUserId) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [players, currentUserId]);
  
  return (
    <div className={`space-y-${isMobile ? '0.5' : '2'}`}>
      <div className="flex items-center justify-between">
        <Label htmlFor="players" className={isMobile ? 'text-xs' : ''}>Players Present</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={isMobile ? 'h-6 w-6 p-0' : 'h-8 w-8 p-0'}
              >
                <Info className={isMobile ? 'h-3 w-3' : 'h-4 w-4'} />
                <span className="sr-only">Player presence info</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[200px]">
              <p className="text-xs">Players present when this score was made. Auto-applied to relevant games.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className={`space-y-1 border rounded-md ${isMobile ? 'p-2.5' : 'p-3'}`}>
        {isLoading ? (
          <div className="flex justify-center items-center py-3">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : players.length > 0 ? (
          sortedPlayers.map(player => (
            <div key={player.id} className={`flex items-center ${isMobile ? 'space-x-3 py-1.5' : 'space-x-2'}`}>
              <Checkbox
                id={`player-${player.id}`}
                checked={selectedPlayerIds.includes(player.id)}
                onCheckedChange={() => onPlayerToggle(player.id)}
                className={isMobile ? 'h-5 w-5' : ''}
                disabled={player.id === currentUserId} // Current user is always present
              />
              <div className="flex items-center space-x-2 w-full">
                <Avatar className={isMobile ? 'h-7 w-7' : 'h-5 w-5'}>
                  <AvatarImage src={player.imageUrl || undefined} alt={player.name} />
                  <AvatarFallback className={`bg-forest text-white ${isMobile ? 'text-sm' : 'text-xs'}`}>
                    {player.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <Label
                  htmlFor={`player-${player.id}`}
                  className={`${isMobile ? 'text-sm' : 'text-sm'} font-normal cursor-pointer`}
                >
                  {player.name} {player.id === currentUserId ? "(You)" : ""}
                </Label>
              </div>
            </div>
          ))
        ) : (
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>
            No players available.
          </p>
        )}
      </div>
    </div>
  );
};
